
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import PanelHeader from "@/components/Reusable/Util/PanelHeader.vue";
import { closeUiPanel } from "@/lib/panels/panels";
import BrowseModeToggle from "@/components/BrowseMode/BrowseModeToggle.vue";
import PanelHeaderButton from "@/components/Reusable/Util/PanelHeaderButton.vue";
import GlobalSettingsPanelControlsViewMode from "@/components/Settings/DocumentOrGlobalSettings/GlobalSettingsPanel/GlobalSettingsPanelControlsViewMode.vue";
import GlobalSettingsPanelHistoryViewMode from "@/components/Settings/DocumentOrGlobalSettings/GlobalSettingsPanel/GlobalSettingsPanelHistoryViewMode.vue";
import { PropertiesData } from "@/types/tree";
import { globalStore, uiStore } from "@/store/store-accessor";
import {
  fetchGlobalSettings,
  saveGlobalSettingsToWordPress
} from "@/lib/global-settings/global-settings";
import defensiveCopy from "@/util/defensive-copy";
import { ClosingConfirmationChosenOption } from "@/types/ui";

@Component({
  components: {
    GlobalSettingsPanelHistoryViewMode,
    GlobalSettingsPanelControlsViewMode,
    PanelHeaderButton,
    BrowseModeToggle,
    PanelHeader
  }
})
export default class GlobalSettingsPanel extends Vue {
  get viewMode() {
    return uiStore.panelState.globalSettingsPanelActiveViewMode;
  }
  toggleViewMode() {
    uiStore.setPanelState({
      ...uiStore.panelState,
      globalSettingsPanelActiveViewMode:
        this.viewMode === "controls" ? "history" : "controls"
    });
  }

  storedData: PropertiesData = null;

  isStoringData = false;
  isLoadingData = false;

  get isLoading() {
    return this.isStoringData || this.isLoadingData;
  }

  async beforeMount() {
    if (
      uiStore.panelState.shouldLoadFreshDataIntoStoreOnceOpened.globalSettings
    ) {
      uiStore.setPanelState({
        ...uiStore.panelState,
        globalSettingsPanelActiveViewMode: "controls"
      });
      await this.loadSettings();
    } else {
      uiStore.setShouldPanelLoadFreshDataIntoStoreOnceOpened(
        "globalSettings",
        true
      );
    }
  }

  async storeSettings() {
    this.isStoringData = true;
    const isSuccessful = await saveGlobalSettingsToWordPress();
    this.storedData = defensiveCopy(globalStore.globalSettings);
    this.isStoringData = false;

    return isSuccessful;
  }

  async loadSettings() {
    this.isLoadingData = true;
    const isSuccessful = await fetchGlobalSettings();
    this.storedData = defensiveCopy(globalStore.globalSettings);
    this.isLoadingData = false;

    return isSuccessful;
  }

  get unsavedChangesPresent(): boolean {
    return globalStore.settingsHasUnsavedChangesPresent;
  }

  requestPanelClosingConfirmation() {
    uiStore.requestClosingConfirmation({
      subject: "globalSettings",
      confirmationDefinition: {
        title: "You have unsaved changes to your Global Settings.",
        text: "Would you like to save or discard these changes?",
        type: "info"
      }
    });
  }

  @Watch("unsavedChangesPresent", { immediate: true })
  handleUnsavedChangesPresentChange(unsavedChangesPresent: boolean) {
    if (unsavedChangesPresent) {
      this.requestPanelClosingConfirmation();
    } else {
      uiStore.noClosingConfirmationIsNeededFor("globalSettings");
    }
  }

  get confirmationChosenOption(): ClosingConfirmationChosenOption | undefined {
    return uiStore.closingConfirmations.globalSettings?.chosenOption;
  }

  @Watch("confirmationChosenOption", { immediate: true })
  async handleConfirmationChosenOptionChange(
    chosenOption: ClosingConfirmationChosenOption | undefined
  ) {
    if (chosenOption === "discard") {
      uiStore.setClosingConfirmationIsDiscarding({
        subject: "globalSettings",
        isDiscarding: true
      });
      if (await this.loadSettings()) {
        this.close();
      } else {
        this.requestPanelClosingConfirmation();
      }
    } else if (chosenOption === "save") {
      uiStore.setClosingConfirmationIsSaving({
        subject: "globalSettings",
        isSaving: true
      });
      if (await this.storeSettings()) {
        this.close();
      } else {
        this.requestPanelClosingConfirmation();
      }
    }
  }

  close() {
    closeUiPanel("globalSettings");
  }
}
